import {
  Label,
  InfoButton,
  Toggle,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import type {
  ChangeEventHandler,
  MouseEventHandler,
} from "react"
import {
  useCallback,
  useMemo,
  useState,
} from "react"

import styles from "./AccountIndex.module.scss"
import { DisableFollowOnlyDialog } from "./DisableFollowOnlyDialog"
import {
  useFollowedUsers,
  usePolyglot,
} from "src/contexts"
import {
  useSharingFollows,
  useSources,
  useUpdateFollow,
} from "src/hooks"
import { FollowOnlyErrorToast } from "src/toasts/FollowOnlyErrorToast"

const polyglotPrefix = "pages.account.follow_only."
const id = "follow-only-mode-toggle"

export function FollowOnlyMode() {
  const polyglot = usePolyglot()
  const { selfFollow } = useFollowedUsers()
  const updateFollow = useUpdateFollow()
  const { showToast } = useToasterContext()

  const { data: sources } = useSources()
  const followers = useSharingFollows()
  const isFollowOnlyToggleDisabled = useMemo(
    () => ((sources && sources.length > 0) || (followers.length > 0)),
    [
      sources,
      followers,
    ],
  )

  const [
    isConfirmOpen,
    setIsConfirmOpen,
  ] = useState<boolean>(false)

  const handleUpdate = useCallback(
    async (value: boolean) => {
      try {
        if (!updateFollow) {
          throw new Error("updateFollow is not defined")
        }

        await updateFollow({
          id: selfFollow.id,
          is_follow_only: value,
        })
      } catch {
        showToast(
          "edit_is_follow_only_error",
          FollowOnlyErrorToast,
        )
      }
    },
    [
      selfFollow.id,
      updateFollow,
      showToast,
    ],
  )

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const value = e.target.checked
      if (value) {
        void handleUpdate(value)
      } else {
        setIsConfirmOpen(true)
      }
    },
    [handleUpdate],
  )

  const onConfirmDisable: MouseEventHandler<HTMLButtonElement> =
    useCallback(
      () => {
        void handleUpdate(false)
      },
      [handleUpdate],
    )

  return (
    <>
      <div className={styles.toggleRow}>
        <Label htmlFor={id}>
          {polyglot.t(`${polyglotPrefix}label`)}
          <InfoButton title={polyglot.t(`${polyglotPrefix}title`)}>
            {polyglot.t(`${polyglotPrefix}description`)}
          </InfoButton>
        </Label>
        <Toggle
          id={id}
          checked={selfFollow.is_follow_only}
          onChange={onChange}
          disabled={isFollowOnlyToggleDisabled}
        />
      </div>
      {isFollowOnlyToggleDisabled && (
        <p className={styles.toggleDisabledDescription}>
          {polyglot.t(`${polyglotPrefix}disabled_description`)}
        </p>
      )}
      <DisableFollowOnlyDialog
        open={isConfirmOpen}
        setOpen={setIsConfirmOpen}
        disable={onConfirmDisable}
      />
    </>
  )
}
